// src/lib/comms.ts — messages, outbox and templates: shapes and calls shared by the pages
// and the nav badge.
import { api } from './api';
import type { ListMeta } from '../types/api';

export type CommsTeam = 'PROPERTY' | 'BUILDING' | 'LETTINGS' | 'ACCOUNTS';
export type Side = 'RESIDENT' | 'STAFF' | 'SYSTEM';

export interface ConversationSummary {
  id: string;
  subject: string;
  team: CommsTeam;
  teamLabel: string;
  /** The team it was sent to first, then any brought in (Task S). */
  teams: Array<{ team: CommsTeam; label: string; original: boolean; addedAt: string | null; addedByName: string | null }>;
  status: 'OPEN' | 'CLOSED';
  propertyId: string;
  propertyName: string | null;
  unitId: string | null;
  unitReference: string | null;
  tenancyId: string | null;
  tenancyReference: string | null;
  residentNames: string | null;
  openedBy: { id: string; name: string | null; side: 'RESIDENT' | 'STAFF' };
  lastMessage: { body: string; side: Side; authorName: string | null; at: string; internal: boolean } | null;
  lastMessageAt: string;
  unread: number;
  awaitingSince: string | null;
  seenByResident: boolean | null;
  closed: { at: string; byName: string | null; reason: string | null } | null;
  createdAt: string;
}

export interface Message {
  id: string;
  side: Side;
  authorId: string | null;
  authorName: string | null;
  body: string;
  internal: boolean;
  at: string;
  mine: boolean;
  seenByResident: boolean | null;
}

export interface ConversationDetail extends ConversationSummary {
  messages: Message[];
  viewer: { side: 'RESIDENT' | 'STAFF'; canReply: boolean; canInternal: boolean; canClose: boolean; canTeams: boolean };
}

export interface Viewer {
  side: 'RESIDENT' | 'STAFF';
  queues: Array<{ team: CommsTeam; label: string }>;
  homes: Array<{ tenancyId: string; label: string }>;
  teams: Array<{ team: CommsTeam; label: string; description: string }>;
}

export interface ConversationList {
  items: ConversationSummary[];
  counts: { open: number; closed: number; unread: number; awaiting: number; byTeam: Record<CommsTeam, { open: number; unread: number }> };
  viewer: Viewer;
}

export type OutboxStatus = 'DRAFT' | 'QUEUED' | 'SENT' | 'FAILED' | 'CANCELLED';
export interface OutboxItem {
  id: string;
  kind: string;
  kindLabel: string;
  channel: 'EMAIL' | 'SMS' | string;
  toEmail: string | null;
  toPhone: string | null;
  toName: string | null;
  subject: string;
  body: string;
  bodyHtml: string | null;
  status: OutboxStatus;
  attempts: number;
  sentAt: string | null;
  transport: string | null;
  transportId: string | null;
  error: string | null;
  unfilled: string[];
  relatedType: string | null;
  relatedId: string | null;
  templateKey: string | null;
  propertyName: string | null;
  tenancyId: string | null;
  tenancyReference: string | null;
  unitReference: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string;
}
export interface OutboxList {
  items: OutboxItem[];
  counts: Record<OutboxStatus, number>;
  transport: string;
  smsProvider?: string;
  kinds: Array<{ key: string; label: string }>;
}

export interface EmailTemplate {
  /** The short text-message wording; null when the letter has none. */
  smsBody: string | null;
  id: string;
  key: string;
  name: string;
  description: string | null;
  team: CommsTeam | null;
  kind: string;
  subject: string;
  body: string;
  isSystem: boolean;
  edited: boolean;
  fields: string[];
  updatedAt: string;
}
export interface MergeFieldGroup { group: string; fields: Array<{ key: string; label: string; sample: string }> }
export interface Rendered { key: string | null; subject: string; body: string; bodyHtml: string; missing: string[]; sample: { tenancyId: string | null; label: string | null } }

export const OUTBOX_TONE: Record<OutboxStatus, 'good' | 'neutral' | 'warning' | 'critical'> = {
  SENT: 'good', QUEUED: 'warning', DRAFT: 'neutral', FAILED: 'critical', CANCELLED: 'neutral',
};

const enc = encodeURIComponent;
export const messagesApi = {
  list: async (q: Record<string, unknown>) => { const r = await api.get<ConversationList>('/messages', q); return { ...r.data, meta: r.meta as ListMeta | undefined }; },
  unread: async () => (await api.get<{ unread: number; awaiting: number }>('/messages/unread')).data,
  get: async (id: string) => (await api.get<ConversationDetail>(`/messages/${enc(id)}`)).data,
  open: async (body: { team: CommsTeam; subject: string; body: string; tenancyId?: string | null }) => (await api.post<ConversationDetail>('/messages', body)).data,
  reply: async (id: string, body: string, internal = false) => (await api.post<ConversationDetail>(`/messages/${enc(id)}/reply`, { body, internal })).data,
  read: (id: string) => api.post(`/messages/${enc(id)}/read`, {}),
  close: async (id: string, reason?: string) => (await api.post<ConversationDetail>(`/messages/${enc(id)}/close`, { reason: reason || null })).data,
  reopen: async (id: string) => (await api.post<ConversationDetail>(`/messages/${enc(id)}/reopen`, {})).data,
  addTeam: async (id: string, team: CommsTeam) => (await api.post<ConversationDetail>(`/messages/${enc(id)}/teams`, { team })).data,
  dropTeam: async (id: string, team: CommsTeam) => (await api.delete<ConversationDetail>(`/messages/${enc(id)}/teams/${team}`)).data,
};

export const outboxApi = {
  list: async (q: Record<string, unknown>) => { const r = await api.get<OutboxList>('/outbox', q); return { ...r.data, meta: r.meta as ListMeta | undefined }; },
  get: async (id: string) => (await api.get<OutboxItem>(`/outbox/${enc(id)}`)).data,
  update: async (id: string, body: Partial<{ toEmail: string | null; toPhone: string | null; toName: string | null; subject: string; body: string }>) => (await api.patch<OutboxItem>(`/outbox/${enc(id)}`, body)).data,
  send: async (id: string) => (await api.post<OutboxItem>(`/outbox/${enc(id)}/send`, {})).data,
  retry: async (id: string) => (await api.post<OutboxItem>(`/outbox/${enc(id)}/retry`, {})).data,
  cancel: async (id: string, reason?: string) => (await api.post<OutboxItem>(`/outbox/${enc(id)}/cancel`, { reason: reason || null })).data,
};

export const templatesApi = {
  list: async () => (await api.get<EmailTemplate[]>('/templates')).data,
  fields: async () => (await api.get<MergeFieldGroup[]>('/templates/fields')).data,
  update: async (key: string, body: { subject: string; body: string; smsBody?: string | null }) => (await api.patch<EmailTemplate>(`/templates/${enc(key)}`, body)).data,
  reset: async (key: string) => (await api.post<EmailTemplate>(`/templates/${enc(key)}/reset`, {})).data,
  render: async (body: { key?: string; subject?: string; body?: string; tenancyId?: string | null }) => (await api.post<Rendered>('/templates/render', body)).data,
};

/** Each team's colour on a thread (the stripe, the team chip). */
export const TEAM_COLOUR: Record<CommsTeam, string> = { PROPERTY: '#5b7fd1', BUILDING: '#3fa7b5', LETTINGS: '#3aa877', ACCOUNTS: '#a07a4f' };

/** "2 min ago" / "14:05" / "3 Oct" for a thread list. */
export function whenShort(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const mins = Math.round((now.getTime() - d.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}
export const whenLong = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
